import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { INDIAN_STATES } from "@/lib/india";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const ORDER_TYPES = ["COD", "Prepaid", "Partial COD"];

const newItem = () => ({
  uid: `it-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  sku_id: "", quantity: 1, selling_price: "",
});

export default function EditOrderDialog({ order, open, onOpenChange, onSaved }) {
  const [skus, setSkus] = useState([]);
  const [form, setForm] = useState({
    customer_name: "", phone: "", address: "", city: "", state: "", pincode: "", email: "",
    order_type: "COD", amount_collected: 0, notes: "",
    other_charges_amount: 0, other_charges_note: "",
    discount_percentage: 0,
  });
  const [items, setItems] = useState([newItem()]);
  const [saving, setSaving] = useState(false);
  const [maxDiscount, setMaxDiscount] = useState(0);

  useEffect(() => {
    if (!open) return;
    api.get("/skus", { params: { active_only: true } }).then(({ data }) => setSkus(data)).catch(() => {});
    api.get("/settings").then(({ data }) => setMaxDiscount(Number(data?.max_discount_percentage || 0))).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!order || !open) return;
    setForm({
      customer_name: order.customer_name || "",
      phone: order.phone || "",
      address: order.address || "",
      city: order.city || "",
      state: order.state || "",
      pincode: order.pincode || "",
      email: order.email || "",
      order_type: order.order_type || "COD",
      amount_collected: order.amount_collected || 0,
      notes: order.notes || "",
      other_charges_amount: order.other_charges_amount || 0,
      other_charges_note: order.other_charges_note || "",
      discount_percentage: order.discount_percentage || 0,
    });
    const existing = (order.items || []).map((i, idx) => ({
      uid: `existing-${idx}`,
      sku_id: i.sku_id,
      quantity: i.quantity,
      selling_price: i.selling_price,
    }));
    setItems(existing.length > 0 ? existing : [newItem()]);
  }, [order, open]);

  if (!order) return null;
  const isClaimed = order.order_source === "Claimed Order";

  const updateItem = (i, field, value) => {
    const next = [...items];
    const sku = skus.find((s) => s.id === (field === "sku_id" ? value : next[i].sku_id));
    next[i] = { ...next[i], [field]: value };
    if (field === "sku_id" && sku?.is_free) {
      next[i].selling_price = 0;
      next[i].quantity = 1;
    } else if (field === "sku_id") {
      next[i].selling_price = "";
    }
    setItems(next);
  };

  const addItem = () => setItems([...items, newItem()]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const lineTotal = (it) => Number(it.quantity || 0) * Number(it.selling_price || 0);
  const total = items.reduce((s, it) => s + lineTotal(it), 0);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount_collected: Number(form.amount_collected || 0),
        other_charges_amount: Number(form.other_charges_amount || 0),
        discount_percentage: Number(form.discount_percentage || 0),
      };
      if (!isClaimed) {
        payload.items = items.map((it) => ({
          sku_id: it.sku_id,
          quantity: Number(it.quantity),
          selling_price: Number(it.selling_price || 0),
        }));
      }
      await api.put(`/orders/${order.id}`, payload);
      toast.success("Order updated");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit order {order.order_id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Customer name</Label>
              <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="edit-name" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Address</Label>
              <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="edit-address" />
            </div>
            <div className="space-y-2">
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="edit-city" />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger data-testid="edit-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {INDIAN_STATES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Pincode</Label>
              <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} data-testid="edit-pincode" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-email" />
            </div>
            <div className="space-y-2">
              <Label>Order type</Label>
              <Select
                value={form.order_type}
                onValueChange={(v) => setForm({ ...form, order_type: v, amount_collected: v === "Partial COD" ? form.amount_collected : 0 })}
              >
                <SelectTrigger data-testid="edit-order-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ORDER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.order_type === "Partial COD" ? (
              <div className="space-y-2">
                <Label>Amount already collected (₹)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.amount_collected}
                  onChange={(e) => setForm({ ...form, amount_collected: e.target.value })}
                  data-testid="edit-amount-collected"
                />
              </div>
            ) : <div />}
            <div className="space-y-2 md:col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-notes" />
            </div>
            <div className="space-y-2">
              <Label>Other charges (₹)</Label>
              <Input type="number" step="0.01" min="0" value={form.other_charges_amount} onChange={(e) => setForm({ ...form, other_charges_amount: e.target.value })} data-testid="edit-other-charges-amount" />
            </div>
            <div className="space-y-2">
              <Label>Other charges description</Label>
              <Input placeholder="e.g. COD handling" value={form.other_charges_note} onChange={(e) => setForm({ ...form, other_charges_note: e.target.value })} data-testid="edit-other-charges-note" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Discount (%) — max {maxDiscount}%</Label>
              <Input
                type="number" step="0.01" min="0" max={maxDiscount}
                disabled={maxDiscount === 0}
                value={form.discount_percentage}
                onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })}
                data-testid="edit-discount"
              />
            </div>
          </div>

          {!isClaimed && (
            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-semibold">Items</h3>
                <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="edit-add-item">
                  <Plus className="w-4 h-4 mr-1" /> Add item
                </Button>
              </div>
              {items.map((it, i) => {
                const sku = skus.find((s) => s.id === it.sku_id);
                return (
                  <div key={it.uid} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border-b border-border pb-3 last:border-0">
                    <div className="md:col-span-5 space-y-2">
                      <Label>SKU</Label>
                      <Select value={it.sku_id} onValueChange={(v) => updateItem(i, "sku_id", v)}>
                        <SelectTrigger><SelectValue placeholder="Select SKU" /></SelectTrigger>
                        <SelectContent>
                          {skus.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.sku_name}
                              {s.is_free && ` · FREE if order ≥ ₹${Number(s.free_min_order_amount || 0)}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <Label>Qty</Label>
                      <Input
                        type="number" min="1"
                        max={sku?.is_free ? 1 : undefined}
                        disabled={sku?.is_free}
                        value={it.quantity}
                        onChange={(e) => updateItem(i, "quantity", e.target.value)}
                      />
                    </div>
                    <div className="md:col-span-3 space-y-2">
                      <Label>Unit price</Label>
                      <Input
                        type="number" step="0.01"
                        disabled={sku?.is_free}
                        value={sku?.is_free ? 0 : it.selling_price}
                        onChange={(e) => updateItem(i, "selling_price", e.target.value)}
                      />
                    </div>
                    <div className="md:col-span-1 text-xs text-muted-foreground">
                      ₹{lineTotal(it).toFixed(2)}
                    </div>
                    <div className="md:col-span-1 text-right">
                      {items.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-end">
                <div className="text-right">
                  <div className="text-xs text-muted-foreground uppercase">New total</div>
                  <div className="font-display font-bold text-xl" data-testid="edit-grand-total">₹{total.toFixed(2)}</div>
                </div>
              </div>
            </div>
          )}
          {isClaimed && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
              This is a claimed order — items can't be modified. Customer info and amounts are still editable.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-order-edit-btn">
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
